import { forwardRef, useCallback, useImperativeHandle } from 'react';
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardCvcElement,
  CardExpiryElement,
} from '@stripe/react-stripe-js';

import { PaymentMethodsType } from '../../@types';
import { SupportedPaymentMethods } from '../../constants';

export const StripeProvider = forwardRef((props, ref) => {
  const stripe = useStripe();
  const elements = useElements();

  const validate = useCallback(() => {
    if (!stripe || !elements) return null;

    const cardElement = elements.getElement(CardNumberElement);
    const cardExpElement = elements.getElement(CardExpiryElement);
    const cardCvcElement = elements.getElement(CardCvcElement);

    if (cardElement) cardElement.blur();
    if (cardExpElement) cardExpElement.blur();
    if (cardCvcElement) cardCvcElement.blur();

    // eslint-disable-next-line no-underscore-dangle
    const isCardValid = cardElement && (cardElement as any)._complete;
    // eslint-disable-next-line no-underscore-dangle
    const isCardExpValid = cardExpElement && (cardExpElement as any)._complete;
    // eslint-disable-next-line no-underscore-dangle
    const isCardCvcValid = cardCvcElement && (cardCvcElement as any)._complete;

    return {
      isCardValid,
      isCardExpValid,
      isCardCvcValid,
    };
  }, [stripe, elements]);

  // TODO: handle errors and undef returns properly
  const processPayment = useCallback(async (data: any) => {
    if (!stripe || !elements) return null;

    const cardElement = elements.getElement(CardNumberElement);

    if (!cardElement) return null;

    const { token, error } = await stripe.createToken(cardElement, {
      name: data.cardholder,
      ...(data.billingDetails?.billingAddress1 && { address_line1: data.billingDetails.billingAddress1 }),
      ...(data.billingDetails?.billingAddress2 && { address_line2: data.billingDetails.billingAddress2 }),
      ...(data.billingDetails?.billingZip && { address_zip: data.billingDetails.billingZip }),
      ...(data.billingDetails?.billingState && { address_state: data.billingDetails.billingState }),
      ...(data.billingDetails?.billingCity && { address_city: data.billingDetails.billingCity }),
      ...(data.billingDetails?.billingCountry && { address_country: data.billingDetails.billingCountry }),
    });

    if (error) return null;

    return token;
  }, [stripe, elements]);

  const createPaymentMethod = useCallback(async (method: PaymentMethodsType, data: any) => {
    if (!stripe || !elements) return null;

    let params: any = {};

    switch (method) {
      case SupportedPaymentMethods.CARD: {
        const cardElement = elements.getElement(CardNumberElement);
        if (!cardElement) return null;
        params = {
          type: 'card',
          card: cardElement,
          billing_details: {
            name: data.cardholder,
            ...(data.billingDetails?.billingEmail && { email: data.billingDetails.billingEmail }),
            address: {
              ...(data.billingDetails?.billingAddress1 && { line1: data.billingDetails.billingAddress1 }),
              ...(data.billingDetails?.billingAddress2 && { line2: data.billingDetails.billingAddress2 }),
              ...(data.billingDetails?.billingZip && { postal_code: data.billingDetails.billingZip }),
              ...(data.billingDetails?.billingState && { state: data.billingDetails.billingState }),
              ...(data.billingDetails?.billingCity && { city: data.billingDetails.billingCity }),
              ...(data.billingDetails?.billingCountry && { country: data.billingDetails.billingCountry }),
            },
          },
        };
        break;
      }
      default: {
        return {
          error: `Creating payment method for ${method} is not supported.`,
        };
      }
    }

    const { error, paymentMethod } = await stripe.createPaymentMethod({ ...params });

    return {
      paymentMethod,
      error: error?.message,
    };
  }, [stripe, elements]);

  useImperativeHandle(ref, () => ({
    createPaymentMethod,
    processPayment,
    validate,
  }), [createPaymentMethod, processPayment, validate]);

  return null;
});
